import {
  GroupSpecificKeyedColours,
  XAxisSpan,
  moodLineChartOptions, createAcademyBarChartOptions
} from "../../../UIPalette/components/Charts/ChartConstants";
import { ReportType } from "../../../UIPalette/components/Reports/ReportConstants";
import { ReactNode, useMemo } from "react";

import { BarChart } from "../../../UIPalette/components/Charts/BarChart";
import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { ExportOnly } from "../../../UIPalette/components/Charts/ExportOnly";
import { LineChart } from "../../../UIPalette/components/Charts/LineChart";
import { PieChart } from "../../../UIPalette/components/Charts/PieChart";
import { Report } from "./Report";
import { RoleKeys } from "core/Constants";
import { formatDimensionWithAverage, sortByKey } from "../../../UIPalette/components/Charts/dimensionLabelFormatter";
import { useAdminUserContext } from "core/state/AdminUserContext";
import { useDefaultLocaleId } from "shared/core/hooks/useDefaultLocaleList";
import { useTranslation } from "react-i18next";
import { yAxisMoodImagePlugin } from "../../../UIPalette/components/Charts/yAxisMoodImagePlugin";
import { ChartFilters } from "src/UIPalette/components/Charts/ChartProps";
import { FilterVisibility } from "../Dashboard";
import { ReportResponseModel } from "shared/request/myHealthyAdvantageApi";
import { transformDimension } from "../../../UIPalette/components/Charts/utils";

export interface DashboardReport {
  title?: string;
  visibilityFilters?: FilterVisibility;
  panel: (filters: ChartFilters) => ReactNode;
}

export interface DashboardReportData {
  report: string;
  children: DashboardReport[];
}

export const courseCompletionTransformer = (data: ReportResponseModel) =>
  data?.dataDimensions
    ? {
        ...data,
        dataDimensions: data.dataDimensions
          .map(transformDimension)
          .sort(sortByKey),
      }
    : data;

export const courseStartsAndCompletesTransformer = (data: ReportResponseModel) =>
  data?.dataDimensions
    ? {
        ...data,
        dataDimensions: [...data.dataDimensions].sort(sortByKey),
      }
    : data;


export const useReportsData = () => {
  const { t } = useTranslation();
  const defaultLocale = useDefaultLocaleId();
  const { isAuthorised } = useAdminUserContext();
  const canViewTotalUsersReport = isAuthorised({ role: RoleKeys.HaAdmin, permissions: ["view-reports"] });

  const reportsData = useMemo<DashboardReportData[]>(() => {
    const averageCompletionBarChartOptions = createAcademyBarChartOptions(t("dashboard.reports.wisdomAcademyMinuteAxis"));
    const startsAndCompletionsBarChartOptions = createAcademyBarChartOptions(t("dashboard.reports.wisdomAcademyYAxis"));

    return [
      {
        report: t("dashboard.reports.usersReport"),
        children: [
          {
            title: t("dashboard.reports.numberOfUsersTab"),
            visibilityFilters: { hideDateFilters: true },
            panel: (filters: ChartFilters) => (
              <>
                <Report title={t("dashboard.reports.numberOfUsersTab")} grouping={filters.groupBy}></Report>
                <PieChart reportType={ReportType.NumberOfUsers} filters={filters} colours={GroupSpecificKeyedColours} />
              </>
            ),
          },
          {
            title: t("dashboard.reports.registrationsTab"),
            panel: (filters: ChartFilters) => (
              <Report title={t("dashboard.reports.registrationsTab")} grouping={filters.groupBy}>
                <PieChart reportType={ReportType.Registrations} filters={filters} colours={GroupSpecificKeyedColours} />
              </Report>
            ),
          },
          {
            title: t("dashboard.reports.dailyActiveUsersTab"),
            panel: (filters: ChartFilters) => (
              <Report title={t("dashboard.reports.dailyActiveUsersTab")} grouping={filters.groupBy}>
                <LineChart reportType={ReportType.DailyActiveUsers} filters={filters} colours={GroupSpecificKeyedColours} xAxisSpan={XAxisSpan.days} />
              </Report>
            ),
          },
          ...(canViewTotalUsersReport
            ? [
                {
                  title: t("dashboard.reports.totalUsersByClientTab"),
                  visibilityFilters: { hideDateFilters: true, hideLocaleFilter: true },
                  panel: (filters: ChartFilters) => (
                    <Report title={t("dashboard.reports.totalUsersByClientTab")}>
                      <ExportOnly reportType={ReportType.TotalUsersByClient} filters={filters} />
                    </Report>
                  ),
                },
              ]
            : []),
        ],
      },
      {
        report: t("dashboard.reports.moodTrackersTab"),
        children: [
          {
            panel: (filters: ChartFilters) => (
              <Report title={t("dashboard.reports.moodTrackersTab")} grouping={filters.groupBy}>
                <LineChart
                  reportType={ReportType.MoodTrackers}
                  filters={filters}
                  colours={GroupSpecificKeyedColours}
                  options={moodLineChartOptions}
                  dimensionLabelFormatter={formatDimensionWithAverage}
                  plugins={[yAxisMoodImagePlugin]}
                />
              </Report>
            ),
          },
        ],
      },
      {
        report: t("dashboard.reports.positiveYouReport"),
        children: [
          {
            title: t("dashboard.reports.dailyJournalEntriesTab"),
            panel: (filters: ChartFilters) => (
              <Report title={t("dashboard.reports.dailyJournalEntriesTab")} grouping={filters.groupBy}>
                <LineChart
                  reportType={ReportType.NumberJournalEntries}
                  filters={filters}
                  colours={GroupSpecificKeyedColours}
                  xAxisSpan={XAxisSpan.days}
                />
              </Report>
            ),
          },
          {
            title: t("dashboard.reports.journalKeyWordsByGroupTab"),
            panel: (filters: ChartFilters) => (
              <Report title={t("dashboard.reports.journalKeyWordsByGroupTab")} grouping={filters.groupBy}>
                <BarChart reportType={ReportType.JournalAttributeGroupUsage} filters={filters} />
              </Report>
            ),
          },
        ],
      },
      {
        report: t("dashboard.reports.wisdomAcademy"),
        children: [
          {
            title: t("dashboard.reports.wisdomAcademyCourseStarts"),
            visibilityFilters: { hideGrouping: true },
            panel: (filters: ChartFilters) => (<Report title={t("dashboard.reports.wisdomAcademyCourseStarts")} grouping={filters.groupBy}>
              <LineChart
                reportType={ReportType.CourseStarts}
                filters={filters}
                xAxisSpan={XAxisSpan.days}
                showSelect
                selectPlaceholder={t("dashboard.reports.wisdomAcademySelectCourse")}
              />
            </Report>)
          },
          {
            title: t("dashboard.reports.wisdomAcademyAverageCourseCompletion"),
            visibilityFilters: { hideGrouping: true },
            panel: (filters: ChartFilters) => (
              <Report title={t("dashboard.reports.wisdomAcademyAverageCourseCompletion")} grouping={filters.groupBy}>
                <BarChart options={averageCompletionBarChartOptions} dataTransformer={courseCompletionTransformer} reportType={ReportType.CourseAverageCompletion} filters={filters} />
              </Report>
            ),
          },
          {
            title: t("dashboard.reports.wisdomAcademyStartsAndCompletions"),
            visibilityFilters: { hideGrouping: true },
            panel: (filters: ChartFilters) => (
              <Report title={t("dashboard.reports.wisdomAcademyStartsAndCompletions")} grouping={filters.groupBy}>
                <BarChart options={startsAndCompletionsBarChartOptions} dataTransformer={courseStartsAndCompletesTransformer} reportType={ReportType.TotalNumberOfCourseStartsAndCompletes} filters={filters} />
              </Report>
            )
          }
        ]
      },
      {
        report: t("dashboard.reports.trackersTab"),
        children: [
          {
            panel: (filters: ChartFilters) => (
              <Report title={t("dashboard.reports.trackersTab")} grouping={filters.groupBy}>
                <BarChart reportType={ReportType.Trackers} filters={filters} translatedLabels={true} />
              </Report>
            ),
          },
        ],
      },
      {
        report: t("dashboard.reports.supportRequestsTab"),
        children: [
          {
            title: t("dashboard.reports.supportRequestsTab"),
            panel: (filters: ChartFilters) => (
              <Report title={t("dashboard.reports.supportRequestsTab")} grouping={filters.groupBy}>
                <BarChart reportType={ReportType.SupportRequests} filters={filters} translatedLabels={true} />
              </Report>
            ),
          },
        ],
      },
      {
        report: t("dashboard.reports.fourWeekPlansTab"),
        children: [
          {
            panel: (filters: ChartFilters) => (
              <Report title={t("dashboard.reports.fourWeekPlansTab")} grouping={filters.groupBy}>
                <BarChart reportType={ReportType.FourWeekPlans} filters={filters} />
              </Report>
            ),
          },
        ],
      },
      {
        report: t("dashboard.reports.miniHealthChecksTab"),
        children: [
          {
            panel: (filters: ChartFilters) => (
              <Report title={t("dashboard.reports.miniHealthChecksTab")} grouping={filters.groupBy}>
                <BarChart reportType={ReportType.MiniHealthChecks} filters={filters} />
              </Report>
            ),
          },
        ],
      },
      {
        report: t("dashboard.reports.preferencesTab"),
        children: [
          {
            visibilityFilters: { hideDateFilters: true },
            panel: (filters: ChartFilters) =>
              defaultLocale ? (
                <Report title={t("dashboard.reports.preferencesTab")} grouping={filters.groupBy}>
                  <BarChart reportType={ReportType.Preferences} filters={filters} />
                </Report>
              ) : (
                <ErrorAlert title={t("dashboard.errors.genericError")} content={t("dashboard.errors.genericErrorBody")} />
              ),
          },
        ],
      }
    ];
}, [t, defaultLocale, canViewTotalUsersReport]);

  return { reportsData };
};
